import React from "react";
import { Link } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useEnquiry } from "@/context/EnquiryContext";
import { img } from "@/api";
import { SocialIcon } from "@/components/BrandIcons";
import { Cog, Mail, Phone, MapPin } from "lucide-react";

export default function Footer() {
  const site = useSite();
  const { open } = useEnquiry();
  const s = (site && site.site) || {};
  const contact = (site && site.contact) || {};
  const categories = (site && site.categories) || [];
  const dev = s.developer_credit || {};

  const footerLogo = img(s.footer_logo || s.logo_url || "");
  const footerLogoH = Number(s.footer_logo_height) || 46;

  // Social links: prefer new editable array, fall back to legacy map.
  let socials = Array.isArray(s.social_links) ? s.social_links.filter((x) => x && x.enabled !== false && x.url && x.url !== "#") : [];
  if (socials.length === 0 && s.social) socials = Object.entries(s.social).filter(([, u]) => u && u !== "#").map(([platform, url], i) => ({ platform, url, order: i }));
  socials = [...socials].sort((a, b) => (a.order || 0) - (b.order || 0));

  // Phones: prefer editable footer phones, fall back to contact phones.
  let phones = Array.isArray(s.footer_phones) ? s.footer_phones.filter((p) => p && p.enabled !== false && p.number) : [];
  if (phones.length === 0) phones = (contact.phones || []).map((n, i) => ({ number: n, order: i }));
  phones = [...phones].sort((a, b) => (a.order || 0) - (b.order || 0));

  const emails = contact.emails || [];
  const unit0 = (contact.units && contact.units[0]) || {};
  const addr = unit0.address || "Rajkot, Gujarat, India";
  const mapsHref = unit0.map && unit0.map.startsWith("http") ? unit0.map.replace("&output=embed", "") : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;

  const linkCls = "transition-colors";
  const linkStyle = { color: "rgba(255,255,255,0.72)" };

  return (
    <footer id="contact-footer" className="relative text-white" style={{ background: "var(--brand-primary)", borderTop: "4px solid var(--yellow)" }} data-testid="site-footer">
      <div className="ekta-container py-14 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-9 lg:gap-10">
        {/* Brand + about + social */}
        <div className="col-span-2 lg:col-span-1">
          <div className="flex items-center gap-2 mb-4">
            {footerLogo ? (
              <img src={footerLogo} alt={s.brand_name} style={{ height: footerLogoH, width: "auto", maxHeight: 90 }} data-testid="footer-logo" />
            ) : (
              <>
                <div className="flex h-11 w-11 items-center justify-center rounded-md" style={{ background: "rgba(255,255,255,0.12)", color: "#fff" }}><Cog size={22} /></div>
                <span className="font-bold text-white" style={{ fontFamily: "var(--font-heading)", fontSize: 15 }}>{s.brand_name || "EKTA WIRE MACHINES"}</span>
              </>
            )}
          </div>
          <p className="text-sm text-justify" style={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.7 }}>{s.footer_about}</p>
          {socials.length > 0 && (
            <>
              <div className="ekta-eyebrow mt-6 mb-3" style={{ color: "var(--yellow)" }}>Follow Us</div>
              <div className="flex gap-3 flex-wrap">
                {socials.map((soc, i) => (
                  <a key={i} href={soc.url || "#"} target="_blank" rel="noreferrer" title={soc.platform} aria-label={soc.platform} className="inline-flex items-center justify-center h-10 w-10 rounded-full transition-colors" style={{ background: "rgba(255,255,255,0.1)" }} data-testid="footer-social"><SocialIcon platform={soc.platform} size={18} color="#fff" /></a>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Machines */}
        <div className="col-span-2 lg:col-span-1">
          <div className="ekta-eyebrow mb-4 whitespace-nowrap" style={{ color: "var(--yellow)" }}>Machines</div>
          <ul className="space-y-2.5 text-sm">
            {categories.map((c) => <li key={c.id}><Link to={`/machines/${c.slug}`} className={`${linkCls} whitespace-nowrap`} style={linkStyle}>{c.name}</Link></li>)}
          </ul>
        </div>

        {/* Quick Links */}
        <div>
          <div className="ekta-eyebrow mb-4" style={{ color: "var(--yellow)" }}>Quick Links</div>
          <ul className="space-y-2.5 text-sm">
            <li><button onClick={() => open({ brochureUrl: " " })} className="text-left transition-colors" style={linkStyle}>Download Brochure</button></li>
            <li><Link to="/measurement-guide" className={linkCls} style={linkStyle}>Measurement Guide</Link></li>
            <li><Link to="/faq" className={linkCls} style={linkStyle}>FAQ</Link></li>
            <li><Link to="/blog" className={linkCls} style={linkStyle}>Blog</Link></li>
            <li><Link to="/about" className={linkCls} style={linkStyle}>About Us</Link></li>
            <li><Link to="/contact" className={linkCls} style={linkStyle}>Contact Us</Link></li>
            <li><Link to="/privacy-policy" className={linkCls} style={linkStyle}>Privacy Policy</Link></li>
            <li><Link to="/disclaimer" className={linkCls} style={linkStyle}>Disclaimer</Link></li>
            <li><Link to="/return-refund-policy" className={linkCls} style={linkStyle}>Return &amp; Refund Policy</Link></li>
          </ul>
        </div>

        {/* Get in Touch */}
        <div>
          <div className="ekta-eyebrow mb-4" style={{ color: "var(--yellow)" }}>Get in Touch</div>
          <ul className="space-y-3 text-sm">
            <li><a href={mapsHref} target="_blank" rel="noreferrer" className="flex gap-2 transition-colors" style={linkStyle} data-testid="footer-address"><MapPin size={16} className="shrink-0 mt-0.5" style={{ color: "var(--yellow)" }} /> {addr}</a></li>
            {phones.map((p, i) => <li key={i}><a href={`tel:${p.number.replace(/[^\d+]/g, "")}`} className="flex gap-2 transition-colors" style={linkStyle} data-testid="footer-phone"><Phone size={16} className="shrink-0 mt-0.5" style={{ color: "var(--yellow)" }} /> {p.number}</a></li>)}
            {emails.map((e) => <li key={e}><a href={`mailto:${e}`} className="flex gap-2 transition-colors" style={linkStyle} data-testid="footer-email"><Mail size={16} className="shrink-0 mt-0.5" style={{ color: "var(--yellow)" }} /> {e}</a></li>)}
          </ul>
        </div>
      </div>

      {/* Copyright */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
        <div className="ekta-container py-1 pb-[62px] sm:py-2.5 sm:pb-2.5 flex flex-row flex-nowrap items-center justify-between gap-2 leading-none">
          {dev.enabled !== false ? (
            <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs leading-none whitespace-nowrap" style={{ color: "rgba(255,255,255,0.6)" }} data-testid="footer-developer-credit">{dev.text || "Developed by"} <a href={dev.url || "#"} className="font-semibold" style={{ color: "var(--yellow)" }}>{dev.name || "[Developer Name]"}</a></span>
          ) : <span />}
          <span className="text-[10px] sm:text-xs leading-none whitespace-nowrap" style={{ color: "rgba(255,255,255,0.6)" }}>
            <span className="sm:hidden">© {new Date().getFullYear()}. All rights reserved.</span>
            <span className="hidden sm:inline">© {new Date().getFullYear()} {s.brand_name || "Ekta Wire Machines"} {s.brand_suffix || ""}. All rights reserved.</span>
          </span>
        </div>
      </div>
    </footer>
  );
}
